import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { createCardAttachment } from "../../../../../../../../services/apiCards";
import useMutate from "../../../../../../../../hooks/useMutate";

const INITIAL_NAME = [{ name: "" }];
const INITIAL_FILE = [{ file: null }];

export function useCardAttachmentDialogState(cardId) {
  const [loading, setLoading] = useState(false);
  const [openAttachFile, setOpenAttachFile] = useState(false);
  const [name, setName] = useState(INITIAL_NAME);
  const [file, setFile] = useState(INITIAL_FILE);
  const queryClient = useQueryClient();
  const { id: boardId } = useParams();
  const [searchParams] = useSearchParams();

  const resetState = useCallback(() => {
    setName(INITIAL_NAME);
    setFile(INITIAL_FILE);
  }, []);

  const { mutate: createCardAttachmentMutate } = useMutate(
    createCardAttachment,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["cardAttachment", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["feeds", String(boardId), searchParams.get("milestone")],
      });
      setLoading(false);
      setOpenAttachFile(false);
      resetState();
    },
    () => {
      setLoading(false);
    }
  );

  const handleOpenAttachFile = useCallback(() => {
    setOpenAttachFile(true);
  }, []);

  const handleCloseAttachFile = useCallback((value) => {
    setOpenAttachFile(value);
    if (!value) {
      resetState();
      setLoading(false);
    }
  }, [resetState]);

  const handleNameChange = useCallback((event, index) => {
    const nextName = [...name];
    nextName[index] = { name: event.target.value };
    setName(nextName);
  }, [name]);

  const handleFileChange = useCallback((event) => {
    if (event.target.files.length > 0) {
      setFile([...event.target.files]);
      return;
    }

    setFile(INITIAL_FILE);
  }, []);

  const canSubmit = useMemo(() => Boolean(file[0]?.name), [file]);

  const handleSubmit = useCallback(() => {
    const formData = file.map((item, index) => ({
      name: name[index]?.name ?? item.name,
      file: file[index],
    }));

    const firstFile = formData[0]?.file;

    if (!firstFile?.name) {
      toast.error("Please upload file");
      return;
    }

    const fileData = new FormData();
    fileData.append("card_id", cardId);
    fileData.append(
      "names",
      formData.map((item) => item.name).join(", ")
    );
    formData.forEach((item) => {
      fileData.append("files", item.file);
    });

    setLoading(true);
    createCardAttachmentMutate(fileData);
  }, [cardId, createCardAttachmentMutate, file, name]);

  return {
    canSubmit,
    file,
    handleCloseAttachFile,
    handleFileChange,
    handleNameChange,
    handleOpenAttachFile,
    handleSubmit,
    loading,
    name,
    openAttachFile,
  };
}
